import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ApiError } from '@/api/client'
import { jobApi, jobPortalApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, PageSpinner, Skeleton } from '@/components/ui/Feedback'
import { PageHeader, ProgressBar, Section } from '@/components/ui/Page'
import { StatStrip } from '@/components/ui/StatStrip'
import { StatusPill, type Tone } from '@/components/ui/StatusPill'
import { MatchRing } from '@/features/jobs/JobCard'
import { PortalLivePanel } from '@/features/profile/PortalHandoffPage'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'

import { setupApi, type SetupPortal, type SetupStage, type SetupStatus } from './api'

const STAGE: Record<SetupStage, { label: string; tone: Tone; live?: boolean }> = {
  creating_account: { label: 'Creating profile', tone: 'info', live: true },
  needs_you: { label: 'Needs you', tone: 'warning' },
  finding_jobs: { label: 'Finding jobs', tone: 'info', live: true },
  ready: { label: 'Ready', tone: 'success' },
  stopped: { label: 'Paused', tone: 'neutral' },
}

/** While something is moving the page follows it closely; afterwards, now and then. */
const POLL_MS = 4000
const IDLE_POLL_MS = 30_000
/** Right after onboarding the set-up may not have written its record yet: wait this long before offering to start it. */
const START_GRACE_MS = 20_000

/**
 * Where a new user lands when onboarding is done: their job search being set
 * up in the background — profiles made on the job portals with their JobSlide
 * address, the jobs read from each, the company career pages matched — with
 * the numbers as they come in, and anything a portal needs from them (a code
 * texted to their phone, a CAPTCHA) asked for right here.
 */
export function GettingStartedPage() {
  const queryClient = useQueryClient()
  const [openedAt] = useState(() => Date.now())
  const status = useQuery({
    queryKey: ['portal-setup'],
    queryFn: setupApi.status,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data?.startedAt) return Date.now() - openedAt < START_GRACE_MS ? 2000 : false
      return data.finished && !data.portals.some((portal) => portal.waiting) ? IDLE_POLL_MS : POLL_MS
    },
  })
  const data = status.data
  const careerMatches = useQuery({
    queryKey: ['top-matches', 'setup', data?.matches.careerPages ?? 0],
    queryFn: () => jobApi.topMatches(5, 0, ['internal']),
    enabled: Boolean(data?.matches.careerPages),
    placeholderData: (previous) => previous,
  })

  const start = useMutation({
    mutationFn: setupApi.start,
    onSuccess: (next) => queryClient.setQueryData(['portal-setup'], next),
    onError: (error: ApiError) => toast.error('Could not start the set-up', error.message),
  })
  const retry = useMutation({
    mutationFn: (key: string) => jobPortalApi.connect(key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-setup'] }),
    onError: (error: ApiError) => toast.error('Could not try again', error.message),
  })

  useNeedsYouAlert(data)

  if (status.isLoading) return <PageSpinner label="Loading" />
  if (!data) return null

  if (!data.startedAt) {
    // Right after onboarding the record lands within seconds (the poll above picks it up); anyone else may start it here.
    return (
      <div className="page-pad-bottom mx-auto max-w-[920px]">
        <PageHeader eyebrow="Your launch checklist" title="Set up your job portals" />
        <EmptyState
          title="Let us make your job portal profiles"
          description="We create a profile for you on each job portal that serves where you live, with your JobSlide email, and read the jobs that match you."
          action={
            <Button onClick={() => start.mutate()} loading={start.isPending}>
              Start the set-up
            </Button>
          }
        />
      </div>
    )
  }

  const total = data.portals.length
  const settled = data.portals.filter((portal) => portal.stage === 'ready' || portal.stage === 'stopped').length
  const waiting = data.portals.filter((portal) => portal.stage === 'needs_you')
  const stopped = data.portals.filter((portal) => portal.stage === 'stopped')
  const elapsedMinutes = Math.max(0, Math.round((Date.now() - Date.parse(data.startedAt)) / 60_000))
  const leftMinutes = Math.max(1, data.estimatedMinutes - elapsedMinutes)

  return (
    <div className="page-pad-bottom mx-auto w-full max-w-[920px] space-y-4">
      <PageHeader
        eyebrow="Your launch checklist"
        title={data.finished ? 'Your job search is set up' : 'We are setting up your job search'}
        description={
          data.finished
            ? 'Your job portal profiles are made and their jobs are in. New jobs keep coming in every few hours.'
            : `We are creating your profiles on ${total} job portals with your JobSlide email and finding the jobs that match you. This takes about ${data.estimatedMinutes} minutes. Please stay on this page: a portal may ask for a code sent to your phone, or a CAPTCHA, and we will ask you right here.`
        }
        actions={
          <Link to="/dashboard" className="w-full sm:w-auto">
            <Button variant={data.finished ? 'primary' : 'secondary'} className="h-11 w-full sm:h-10 sm:w-auto">
              {data.finished ? 'Go to my matches' : 'Go to my dashboard'}
            </Button>
          </Link>
        }
      />

      {/* Progress: how many portals are done, and about how long is left. */}
      <section className="card space-y-2.5 px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[14px] font-medium text-ink">
            <span className="num">{settled}</span> of <span className="num">{total}</span> job portals done
          </p>
          <p className="text-[12.5px] text-ink-muted">
            {data.finished ? (
              'Finished'
            ) : (
              <>
                About <span className="num">{leftMinutes}</span> min left · started <span className="num">{elapsedMinutes}</span> min ago
              </>
            )}
          </p>
        </div>
        <ProgressBar value={total ? (settled / total) * 100 : 0} label="Job portals done" tone={waiting.length ? 'warning' : 'brand'} />
        {!data.finished && <NotifyMe />}
      </section>

      {data.needsPassword && (
        <Callout tone="warning" title="Set your portal password">
          We make each job portal profile with your JobSlide email and one portal password. Set it once and the paused portals carry on.{' '}
          <Link to="/dashboard/settings" className="font-medium text-brand hover:underline">
            Set it in Settings
          </Link>
        </Callout>
      )}

      {/* What waits for the person comes first: a code, a CAPTCHA. */}
      {waiting.map((portal) => (
        <WaitingPortal key={portal.key} portal={portal} />
      ))}

      <StatStrip
        segments={[
          { key: 'career', label: 'Company career pages', value: data.matches.careerPages, tone: 'brand' },
          { key: 'portals', label: 'Job portals', value: data.matches.portals, tone: 'info', live: !data.finished },
          ...(data.matches.naukri ? [{ key: 'naukri', label: 'Naukri', value: data.matches.naukri, tone: 'info' as Tone }] : []),
          {
            key: 'apply',
            label: data.applications.planRemaining === null ? 'Matched jobs' : 'You can apply to',
            value: data.applications.possible,
            tone: 'success',
          },
        ]}
      />
      <p className="-mt-2 px-1 text-[12px] text-ink-muted">
        <span className="num">{data.matches.total}</span> matched jobs so far
        {data.applications.planRemaining !== null && (
          <>
            {' '}
            · your plan has <span className="num">{data.applications.planRemaining}</span> applications left this month
          </>
        )}
        .
      </p>

      <Section
        title="Company matches"
        description="Jobs from company career pages that fit your profile"
        actions={
          data.matches.careerPages > 0 ? (
            <Link to="/dashboard" className="text-[13px] font-medium text-brand hover:underline">
              See all
            </Link>
          ) : undefined
        }
        bodyClassName="p-0"
      >
        {data.matches.careerPages === 0 && data.careerPagesNote ? (
          <div className="space-y-2 px-5 py-4">
            <p className="text-[13.5px] text-ink-soft">{data.careerPagesNote}.</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/dashboard/profile/preferences" className="text-[13px] font-medium text-brand hover:underline">
                Add roles in Preferences
              </Link>
              <Button size="sm" variant="secondary" onClick={() => start.mutate()} loading={start.isPending} className="h-11 sm:h-8">
                Match again
              </Button>
            </div>
          </div>
        ) : data.matches.careerPages === 0 ? (
          <p className="px-5 py-4 text-[13.5px] text-ink-muted">Matching company career pages to your profile…</p>
        ) : careerMatches.isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {(careerMatches.data ?? []).map((match) => (
              <li key={match.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
                <MatchRing score={match.matchScore} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-medium text-ink">{match.job.title}</p>
                  <p className="truncate text-[12.5px] text-ink-muted">
                    {match.job.company?.name ?? match.job.companyName}
                    {match.job.location ? ` · ${match.job.location}` : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Job portals" description="A profile on each, made with your JobSlide email" bodyClassName="p-0">
        <ul className="divide-y divide-line">
          {data.portals.map((portal) => (
            <PortalRow key={portal.key} portal={portal} onRetry={() => retry.mutate(portal.key)} retrying={retry.isPending && retry.variables === portal.key} />
          ))}
        </ul>
        {stopped.length > 0 && (
          <p className="border-t border-line px-4 py-3 text-[12.5px] text-ink-muted sm:px-5">
            A paused portal is not lost: try it again here, or later from Profile → Job Portals.
          </p>
        )}
      </Section>
    </div>
  )
}

function PortalRow({ portal, onRetry, retrying }: { portal: SetupPortal; onRetry: () => void; retrying: boolean }) {
  const stage = STAGE[portal.stage]
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 sm:px-5">
      <div className="min-w-0 flex-1 basis-[200px]">
        <p className="truncate text-[14px] font-medium text-ink">{portal.name}</p>
        <p className={cn('text-[12.5px] leading-snug', portal.stage === 'stopped' ? 'text-ink-soft' : 'text-ink-muted')}>
          {portal.stage === 'creating_account'
            ? 'Creating your profile and filling it from yours…'
            : portal.stage === 'finding_jobs'
              ? 'Reading the jobs that match you…'
              : portal.stage === 'needs_you'
                ? 'Waiting for you above'
                : portal.message ??
                  (portal.accountIdentifier ? `Profile made with ${portal.accountIdentifier}` : portal.needsAccount ? 'Profile made' : 'Read without an account')}
        </p>
      </div>
      {portal.jobCount > 0 && (
        <span className="num shrink-0 text-[13px] text-ink-soft">
          {portal.jobCount} {portal.jobCount === 1 ? 'job' : 'jobs'}
        </span>
      )}
      <StatusPill tone={stage.tone} live={stage.live}>
        {stage.label}
      </StatusPill>
      {portal.stage === 'stopped' && portal.needsAccount && (
        <Button size="sm" variant="secondary" onClick={onRetry} loading={retrying} className="h-11 sm:h-8">
          Try again
        </Button>
      )}
    </li>
  )
}

/** A portal parked on a step only the person can do, opened right here. */
function WaitingPortal({ portal }: { portal: SetupPortal }) {
  const queryClient = useQueryClient()
  const done = useMutation({
    mutationFn: () => jobPortalApi.handoffDone(portal.key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-setup'] }),
    onError: () => toast.error('That page is no longer waiting'),
  })
  const sms = portal.waiting?.reason === 'sms'
  return (
    <section className="space-y-2" aria-live="polite">
      <Callout tone="warning" title={sms ? `${portal.name} sent a code to your phone` : `${portal.name} needs you for a moment`}>
        {portal.message ?? (sms ? 'Type the code below and we will finish the profile.' : 'Finish the check on the page below and we will carry on.')}
      </Callout>
      <PortalLivePanel
        portalKey={portal.key}
        name={portal.name}
        actions={
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pt-1">
            <p className="min-w-0 flex-1 text-[12px] leading-snug text-ink-muted">When the step is done the set-up continues by itself.</p>
            <Button size="sm" variant="secondary" onClick={() => done.mutate()} loading={done.isPending} className="h-11 sm:h-9">
              I have finished this step
            </Button>
          </div>
        }
        idle={<Skeleton className="h-24" />}
      />
    </section>
  )
}

function Callout({ tone, title, children }: { tone: 'warning' | 'info'; title: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-card border px-4 py-3 sm:px-5', tone === 'warning' ? 'border-warning/40 bg-warning/[0.08]' : 'border-info/40 bg-info/[0.08]')}>
      <p className="text-[14px] font-semibold text-ink">{title}</p>
      <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">{children}</p>
    </div>
  )
}

/** A portal that starts waiting for the person: a sound, the tab title, and (when allowed) a desktop notification. */
function useNeedsYouAlert(data: SetupStatus | undefined) {
  const announced = useRef(new Set<string>())
  const waiting = (data?.portals ?? []).filter((portal) => portal.waiting)
  const ids = waiting.map((portal) => portal.key).join(',')

  useEffect(() => {
    const fresh = waiting.filter((portal) => !announced.current.has(portal.key))
    for (const portal of waiting) announced.current.add(portal.key)
    for (const key of [...announced.current]) if (!waiting.some((portal) => portal.key === key)) announced.current.delete(key)
    const first = fresh[0]
    if (!first) return
    chime()
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
      try {
        new Notification(`${first.name} needs you`, { body: first.waiting?.reason === 'sms' ? 'Enter the code sent to your phone.' : 'Finish a quick check to continue.' })
      } catch {
        /* not allowed here */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids])

  useEffect(() => {
    if (!waiting.length) return
    const before = document.title
    document.title = `(${waiting.length}) Needs you · ${before.replace(/^\(\d+\) Needs you · /, '')}`
    return () => {
      document.title = before
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids])
}

/** "Tell me when a portal needs me", offered while the set-up runs and the browser has not been asked yet. */
function NotifyMe() {
  const [permission, setPermission] = useState(() => (typeof Notification === 'undefined' ? 'denied' : Notification.permission))
  if (permission !== 'default') return null
  return (
    <button
      type="button"
      onClick={() => void Notification.requestPermission().then(setPermission).catch(() => undefined)}
      className="tap text-[12.5px] font-medium text-brand hover:underline"
    >
      Notify me when a portal needs me
    </button>
  )
}

function chime() {
  try {
    const Context = window.AudioContext
    if (!Context) return
    const context = new Context()
    const now = context.currentTime
    for (const [offset, frequency] of [[0, 880], [0.18, 1175]] as const) {
      const oscillator = context.createOscillator()
      const gain = context.createGain()
      oscillator.frequency.value = frequency
      gain.gain.setValueAtTime(0.0001, now + offset)
      gain.gain.exponentialRampToValueAtTime(0.2, now + offset + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.16)
      oscillator.connect(gain).connect(context.destination)
      oscillator.start(now + offset)
      oscillator.stop(now + offset + 0.18)
    }
    window.setTimeout(() => void context.close(), 800)
  } catch {
    // No sound is fine: the page and the tab title still say it.
  }
}
